// src/app/shared/services/websocket.service.ts (Conceptual Example)
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, timer, throwError } from 'rxjs';
import { filter, takeUntil, retry, delay, tap } from 'rxjs/operators';

export interface WebSocketConfig {
  maxReconnectAttempts?: number;
  reconnectInterval?: number;
  heartbeatInterval?: number;
  messageTimeout?: number;
}

@Injectable({
  providedIn: 'root'
})
export class WebsocketService {
  private webSocket: WebSocket | undefined;
  private notificationsSubject = new Subject<string>();
  private isConnectedSubject = new BehaviorSubject<boolean>(false);
  private statusMessageSubject = new BehaviorSubject<string>('Disconnected.');
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 5000; // 5 seconds
  private heartbeatInterval = 30000; // 30 seconds
  private messageTimeout = 60000; // 60 seconds
  private heartbeatTimer: any;
  private messageTimeoutTimer: any;
  private lastMessageTime = Date.now();
  private shouldReconnect = false;
  private currentUrl = '';
  private destroy$ = new Subject<void>();

  constructor() {
    // Start monitoring for message timeouts
    this.startMessageTimeoutMonitoring();
  }

  /**
   * Initiates a WebSocket connection to the given URL with optional configuration.
   * @param url The WebSocket URL (e.g., ws://localhost:7067/ws/someId)
   * @param config Optional configuration for the WebSocket connection.
   */
  public connect(url: string, config?: WebSocketConfig): void {
    if (config) {
      this.maxReconnectAttempts = config.maxReconnectAttempts ?? this.maxReconnectAttempts;
      this.reconnectInterval = config.reconnectInterval ?? this.reconnectInterval;
      this.heartbeatInterval = config.heartbeatInterval ?? this.heartbeatInterval;
      this.messageTimeout = config.messageTimeout ?? this.messageTimeout;
    }

    this.shouldReconnect = true;
    this.currentUrl = url;
    this.reconnectAttempts = 0;
    this.connectInternal();
  }

  private connectInternal(): void {
    if (this.webSocket && (this.webSocket.readyState === WebSocket.OPEN || this.webSocket.readyState === WebSocket.CONNECTING)) {
      console.warn('WebSocket is already open or connecting. Disconnecting first.');
      this.disconnectInternal();
    }

    this.updateStatus(`Connecting... (Attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);
    this.isConnectedSubject.next(false);

    try {
      this.webSocket = new WebSocket(this.currentUrl);
      this.setupWebSocketHandlers();
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      this.handleConnectionFailure();
    }
  }

  private setupWebSocketHandlers(): void {
    if (!this.webSocket) return;

    this.webSocket.onopen = (event) => {
      this.reconnectAttempts = 0;
      this.isConnectedSubject.next(true);
      this.updateStatus('Connected and ready.');
      this.lastMessageTime = Date.now();
      this.startHeartbeat();
    };

    this.webSocket.onmessage = (event) => {
      this.lastMessageTime = Date.now();
      
      // Handle heartbeat messages
      if (event.data === 'ping') {
        this.sendPong();
        return;
      }
      
      // Emit regular messages
      this.notificationsSubject.next(event.data);
    };

    this.webSocket.onclose = (event) => {
      this.isConnectedSubject.next(false);
      this.stopHeartbeat();
      
      if (event.wasClean) {
        this.updateStatus('Disconnected cleanly.');
      } else {
        this.updateStatus(`Disconnected unexpectedly. Code: ${event.code}, Reason: ${event.reason}`);
        console.warn('WebSocket closed unexpectedly:', event.code, event.reason);
        
        if (this.shouldReconnect) {
          this.handleConnectionFailure();
        }
      }
      
      this.webSocket = undefined;
    };

    this.webSocket.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.isConnectedSubject.next(false);
      this.updateStatus('Connection error occurred.');
      
      if (this.shouldReconnect) {
        this.handleConnectionFailure();
      }
    };
  }

  private handleConnectionFailure(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts && this.shouldReconnect) {
      this.reconnectAttempts++;
      const delay = this.reconnectInterval * Math.pow(1.5, this.reconnectAttempts - 1); // Exponential backoff
      
      this.updateStatus(`Reconnecting in ${delay/1000} seconds... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      
      setTimeout(() => {
        if (this.shouldReconnect) {
          this.connectInternal();
        }
      }, delay);
    } else {
      this.updateStatus('Connection failed. Maximum reconnection attempts reached.');
      this.shouldReconnect = false;
      
      // Emit error notification for UI handling
      this.notificationsSubject.next(JSON.stringify({
        Status: 'CONNECTION_FAILED',
        Message: 'Unable to establish WebSocket connection. Please refresh the page and try again.'
      }));
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
        this.sendPing();
      }
    }, this.heartbeatInterval);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private sendPing(): void {
    try {
      if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
        this.webSocket.send('ping');
      }
    } catch (error) {
      console.error('Failed to send ping:', error);
    }
  }

  private sendPong(): void {
    try {
      if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
        this.webSocket.send('pong');
      }
    } catch (error) {
      console.error('Failed to send pong:', error);
    }
  }

  private startMessageTimeoutMonitoring(): void {
    this.messageTimeoutTimer = setInterval(() => {
      if (this.isConnectedSubject.value && (Date.now() - this.lastMessageTime) > this.messageTimeout) {
        console.warn('Message timeout detected. No messages received for', this.messageTimeout, 'ms');
        this.updateStatus('Connection appears to be stale. Attempting to reconnect...');
        
        // Emit timeout notification
        this.notificationsSubject.next(JSON.stringify({
          Status: 'TIMEOUT',
          Message: 'Connection timeout. Attempting to reconnect...'
        }));
        
        if (this.shouldReconnect) {
          this.disconnectInternal();
          this.connectInternal();
        }
      }
    }, 10000); // Check every 10 seconds
  }

  /**
   * Disconnects the current WebSocket connection.
   */
  public disconnect(): void {
    this.shouldReconnect = false;
    this.disconnectInternal();
  }

  private disconnectInternal(): void {
    this.stopHeartbeat();
    
    if (this.webSocket) {
      if (this.webSocket.readyState === WebSocket.OPEN) {
        this.webSocket.close(1000, 'Client initiated disconnect');
      } else {
        // Clear handlers to prevent unwanted callbacks
        this.webSocket.onopen = null;
        this.webSocket.onmessage = null;
        this.webSocket.onclose = null;
        this.webSocket.onerror = null;
        this.webSocket.close();
      }
      this.webSocket = undefined;
    }
    
    this.isConnectedSubject.next(false);
    this.updateStatus('Disconnected.');
  }

  /**
   * Returns an Observable for incoming WebSocket messages.
   */
  public getNotifications(): Observable<string> {
    return this.notificationsSubject.asObservable();
  }

  /**
   * Returns an Observable for the WebSocket connection status (true if connected).
   */
  public getIsConnected(): Observable<boolean> {
    return this.isConnectedSubject.asObservable();
  }

  /**
   * Returns an Observable for detailed status messages.
   */
  public getStatusMessage(): Observable<string> {
    return this.statusMessageSubject.asObservable();
  }

  /**
   * Updates the internal status message and emits it.
   * @param message The status message to set.
   */
  public updateStatus(message: string): void {
    this.statusMessageSubject.next(message);
  }

  /**
   * Sends a message through the WebSocket.
   * @param message The message to send.
   */
  public sendMessage(message: string): boolean {
    if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
      try {
        this.webSocket.send(message);
        return true;
      } catch (error) {
        console.error('Failed to send message:', error);
        this.updateStatus('Failed to send message.');
        return false;
      }
    } else {
      console.warn('WebSocket is not open. Cannot send message:', message);
      this.updateStatus('Cannot send message: WebSocket not open.');
      return false;
    }
  }

  /**
   * Forcibly reconnects the WebSocket if reconnection is allowed.
   */
  public forceReconnect(): void {
    if (this.shouldReconnect) {
      this.updateStatus('Force reconnecting...');
      this.disconnectInternal();
      this.connectInternal();
    }
  }

  /**
   * Cleans up the service, disconnects the WebSocket, and stops reconnection attempts.
   */
  public destroy(): void {
    this.shouldReconnect = false;
    this.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
    
    if (this.messageTimeoutTimer) {
      clearInterval(this.messageTimeoutTimer);
    }
  }
}
